/** Strip characters that could break out of a CSS value context */
export const sanitizeCssValue = (v: string): string =>
  v.replace(/[{}<>;"'\\]/g, "").replace(/\/\*/g, "").replace(/\*\//g, "").trim();

export type BtnHoverProps = {
  btnHoverEffect: string;
  btnHoverBgColor: string;
  btnHoverTextColor: string;
  btnHoverShadow: string;
  btnHoverScale: string;
  btnHoverTransitionDuration: string;
};

export const parseBtnHoverProps = (container: HTMLElement): BtnHoverProps => ({
  btnHoverEffect: container.getAttribute("data-btn-hover-effect") || "brightness",
  btnHoverBgColor: container.getAttribute("data-btn-hover-bg-color") || "",
  btnHoverTextColor: container.getAttribute("data-btn-hover-text-color") || "",
  btnHoverShadow: container.getAttribute("data-btn-hover-shadow") || "0 4px 12px rgba(0,0,0,0.15)",
  btnHoverScale: container.getAttribute("data-btn-hover-scale") || "1.03",
  btnHoverTransitionDuration: container.getAttribute("data-btn-hover-transition") || "0.2s",
});

/**
 * Generate scoped CSS for button hover effects.
 * @param scopeSelector - CSS selector prefix, e.g. `.ez-cl-abc123` or `[data-ez-certificate-list]`
 * @param btnSelector - button CSS selector within scope, e.g. `button` or `.material-card-main-btn`
 * @param props - hover settings
 */
export const generateBtnHoverCss = (
  scopeSelector: string,
  btnSelector: string,
  props: BtnHoverProps,
): string => {
  const dur = sanitizeCssValue(props.btnHoverTransitionDuration || "0.2s");
  const effect = props.btnHoverEffect || "brightness";

  if (effect === "none") return "";

  // Split comma-separated selectors and also target nested button/a inside each
  const selectors = btnSelector.split(",").map(s => s.trim());
  const allSelectors = selectors.flatMap(s => [s, `${s} button`, `${s} a`]);
  const selectorStr = allSelectors.map(s => `${scopeSelector} ${s}`).join(",\n    ");
  const hoverStr = allSelectors.map(s => `${scopeSelector} ${s}:hover`).join(",\n    ");

  let baseCss = `
    ${selectorStr} {
      transition: all ${dur} ease !important;
    }
  `;

  switch (effect) {
    case "colorChange":
      baseCss += `
        ${hoverStr} {
          ${props.btnHoverBgColor ? `background-color: ${sanitizeCssValue(props.btnHoverBgColor)} !important; border-color: ${sanitizeCssValue(props.btnHoverBgColor)} !important;` : "opacity: 0.85;"}
          ${props.btnHoverTextColor ? `color: ${sanitizeCssValue(props.btnHoverTextColor)} !important;` : ""}
        }
      `;
      break;
    case "shadow":
      baseCss += `
        ${hoverStr} {
          box-shadow: ${sanitizeCssValue(props.btnHoverShadow || "0 4px 12px rgba(0,0,0,0.15)")} !important;
          transform: translateY(-1px) !important;
        }
      `;
      break;
    case "scale":
      baseCss += `
        ${hoverStr} {
          transform: scale(${sanitizeCssValue(props.btnHoverScale || "1.03")}) !important;
        }
      `;
      break;
    case "brightness":
    default:
      baseCss += `
        ${hoverStr} {
          filter: brightness(1.15) !important;
          transform: translateY(-1px) !important;
        }
      `;
      break;
  }

  return baseCss;
};

/**
 * Generate inline hover style object for buttons rendered with inline styles (not scoped CSS).
 * Use with onMouseEnter/onMouseLeave handlers.
 */
export const getBtnHoverStyle = (props: BtnHoverProps): React.CSSProperties => {
  const effect = props.btnHoverEffect || "brightness";
  switch (effect) {
    case "colorChange":
      return {
        ...(props.btnHoverBgColor ? { backgroundColor: props.btnHoverBgColor, borderColor: props.btnHoverBgColor } : { opacity: 0.85 }),
        ...(props.btnHoverTextColor ? { color: props.btnHoverTextColor } : {}),
      };
    case "shadow":
      return { boxShadow: props.btnHoverShadow || "0 4px 12px rgba(0,0,0,0.15)", transform: "translateY(-1px)" };
    case "scale":
      return { transform: `scale(${props.btnHoverScale || "1.03"})` };
    case "brightness":
      return { filter: "brightness(1.1)" };
    default:
      return {};
  }
};
